import { dailyRng, jitter } from './collections/daily.ts'
import { buildDateRow } from './collections/dateRow.ts'
import { buildFreeRow } from './collections/freeRow.ts'
import { buildKidsRow } from './collections/kidsRow.ts'
import { buildDrivingRow } from './collections/nearbyDrivingRow.ts'
import { buildWalkingRow } from './collections/nearbyWalkingRow.ts'
import { buildRecentRow } from './collections/recentRow.ts'
// import { buildStaleRow } from './collections/staleRow.ts'
import {
	type BuilderCtx,
	type Collection,
	type CollectionBuilder,
} from './collections/shared.ts'
import { buildWeatherRow } from './collections/weatherRow.ts'

export type { Collection } from './collections/shared.ts'

// Row order on the Explore landing. "Good for today" leads — it's the most
// timely. "Not done in a while" (buildStaleRow) is parked for now: low value
// until there's more visit history, but kept a comment away from returning.
// Each builder lives in its own file under collections/ so the list reads as a
// table of contents.
const BUILDERS: CollectionBuilder[] = [
	buildWeatherRow,
	buildWalkingRow,
	buildDrivingRow,
	// buildStaleRow,
	buildRecentRow,
	buildFreeRow,
	buildKidsRow,
	buildDateRow,
]

// How far rows / cards may drift from their natural order each day — kept small
// so the page feels fresh without losing the meaningful sort.
const ROW_JITTER = 1.5
const ITEM_JITTER = 2

// Runs every builder, dropping the ones that don't apply (no location yet) or
// come back empty, then injects a small daily-seeded shuffle: the cards within
// each row, and the row order itself ("Good for today" stays pinned to the
// front). One RNG stream means the result is stable across revisits on the same
// day and different the next.
export const buildCollections = (ctx: BuilderCtx): Collection[] => {
	const rng = dailyRng()

	const built = BUILDERS.map((build) => build(ctx))
		.filter((c): c is Collection => c !== null && c.activities.length > 0)
		.map((c) => ({
			...c,
			activities: jitter(c.activities, rng, ITEM_JITTER),
		}))

	const lead = built.filter((c) => c.key === 'weather')
	const rest = jitter(
		built.filter((c) => c.key !== 'weather'),
		rng,
		ROW_JITTER,
	)
	return [...lead, ...rest]
}
